import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Button, Container, IconButton, Skeleton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { getData, postData } from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";
import { getToken } from "../../Utils/authHelper";
import { resolveOfferImage } from "../../Components/OfferImage/OfferImage";
import ThumbStrip from "../../Components/ThumbStrip/ThumbStrip";
import BogoCard from "../../Components/BogoCard/BogoCard";
import ProductCard from "../../Components/ProductCard/ProductCard";
import SectionHeading from "../../Components/SectionHeading/SectionHeading";

const rubikfont = { fontFamily: "Rubik, sans-serif" };
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const MAX_SETS = 5;

// product text is written as HTML in the admin panel; keep its formatting but drop anything that can run code
const cleanHtml = (html) => String(html || "")
  .replace(/<\s*(script|style|iframe|object|embed)[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
  .replace(/<\s*(script|style|iframe|object|embed)[^>]*>/gi, "")
  .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
  .replace(/(href|src)\s*=\s*("|')\s*javascript:[^"']*\2/gi, "$1=$2#$2");

// The separate product page for a BOGO deal: the offer's pictures, the price split at MRP, and one button that adds the deal.
const BogoDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchCartCount } = useCart();

  const [bogo, setBogo] = useState(location.state?.bogo || null);
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState(0);
  const [sets, setSets] = useState(1);
  const [image, setImage] = useState(0);
  const [adding, setAdding] = useState(false);
  const [otherDeals, setOtherDeals] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [fullProduct, setFullProduct] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    getData(`bogo-offer/${id}`, {}, "", false)
      .then((res) => { if (res?.success && res.data) setBogo(res.data); else if (!bogo) { toast.error("This deal could not be found"); navigate("/bogo-deals"); } })
      .catch(() => { if (!bogo) { toast.error("This deal could not be found"); navigate("/bogo-deals"); } })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const products = useMemo(() => (bogo?.buyProducts || []).map((x) => x.product_id).filter((p) => p && typeof p === "object"), [bogo]);
  const categories = useMemo(() => (bogo?.buyProducts || []).map((x) => x.category_id).filter((c) => c && typeof c === "object"), [bogo]);
  const product = products[Math.min(pick, Math.max(products.length - 1, 0))] || null;
  const reward = bogo?.getProducts?.[0]?.product_id || product;

  useEffect(() => { setImage(0); }, [pick, bogo?._id]);

  // other live deals
  useEffect(() => {
    getData("bogo-offer/all", {}, "", false)
      .then((res) => setOtherDeals(res?.success && Array.isArray(res.data) ? res.data.filter((b) => b._id !== id) : []))
      .catch(() => setOtherDeals([]));
  }, [id]);

  // products like the one in the deal (the same lists the regular product page shows)
  const productId = product?._id;
  useEffect(() => {
    if (!productId) { setSimilarProducts([]); setRelatedProducts([]); setFullProduct(null); return; }
    const tokenValue = getToken();
    getData(`product/view/${productId}`, {}, tokenValue ? `Bearer ${tokenValue}` : "", false)
      .then((res) => {
        setFullProduct(res?.product || null);
        setSimilarProducts(res?.similar?.productsSimilar || []);
        setRelatedProducts(res?.similar?.productsRelated || []);
      })
      .catch(() => { setSimilarProducts([]); setRelatedProducts([]); setFullProduct(null); });
  }, [productId]);

  if (loading && !bogo) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "1.1fr 1.3fr 0.8fr" } }}>
          <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        </Box>
      </Container>
    );
  }
  if (!bogo) return null;

  const buyQty = Number(bogo.buyQuantity) || 1;
  const getQty = Number(bogo.getQuantity) || 1;
  const pct = Number(bogo.discountPercentage) || 100;
  const free = pct >= 100;

  const units = sets * buyQty;
  const freeUnits = sets * getQty;
  const buyMrp = Number(product?.b2c_mrp) || 0;
  const rewardMrp = Number(reward?.b2c_mrp) || buyMrp;
  const buyTotal = buyMrp * units;
  const rewardFull = rewardMrp * freeUnits;
  const rewardOff = (rewardFull * pct) / 100;
  const youPay = buyTotal + rewardFull - rewardOff;
  const totalUnits = units + freeUnits;

  // pictures: the offer's own first, then the chosen product's
  const pictures = [
    ...(bogo.product_images || []).map((src) => ({ src: resolveOfferImage(src), photo: true })),
    ...(product?.product_images || []).map((src) => ({ src: resolveOfferImage(src), photo: false })),
  ].filter((x) => x.src);
  const shown = pictures[Math.min(image, Math.max(pictures.length - 1, 0))];

  const stockLeft = bogo.stockLeft;
  const available = bogo.inStock !== false && (!product || product.inStock !== false);
  const maxSets = Math.max(1, Math.min(MAX_SETS, typeof stockLeft === "number" ? stockLeft : MAX_SETS));
  const details = { ...(product || {}), ...(fullProduct || {}) };
  const specs = [
    { label: "Brand", value: details.brand },
    { label: "Category", value: details.category_id?.title },
    { label: "Unit", value: details.unitValue ? `${details.unitValue} ${details.unitName || ""}` : null },
    { label: "Container", value: details.container_type },
    { label: "Ideal For", value: details.ideal_for },
    { label: "Skin Type", value: details.skin_type },
    { label: "Hair Type", value: details.hair_type },
    { label: "Country of Origin", value: details.country_of_orgin },
  ].filter((x) => x.value);
  const hasDetails = specs.length > 0 || details.key_features || details.description || details.how_to_use;

  const addDeal = async () => {
    const tokenValue = getToken();
    const token = tokenValue ? `Bearer ${tokenValue}` : "";
    if (!token) { navigate("/signin"); return; }
    if (!product) { navigate("/products"); return; }
    try {
      setAdding(true);
      const res = await postData("cart/add-to-cart", { product_id: product._id, quantity: units }, token, false, false);
      if (res && (res.success || res.cart || res.message === "Cart updated")) {
        fetchCartCount();
        toast.success(`🎉 Added ${units} item${units > 1 ? "s" : ""}. Your ${freeUnits} ${free ? "free" : "discounted"} item${freeUnits > 1 ? "s are" : " is"} applied automatically.`);
      } else if (res?.message) toast.error(res.message);
    } catch (e) {
      toast.error("Could not add the deal. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const dealTag = free ? `BUY ${buyQty} GET ${getQty} FREE` : `BUY ${buyQty} GET ${getQty} (${pct}% OFF)`;
  const stockText = !available ? "Currently out of stock" : typeof stockLeft === "number" && stockLeft <= 5 ? `Only ${stockLeft} deal${stockLeft === 1 ? "" : "s"} left` : "In stock";

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "70vh", ...rubikfont }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "grid", gap: { xs: 2.5, md: 5 }, gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 540px) minmax(0, 1fr)" }, alignItems: "start", justifyContent: "center" }}>
          {/* Pictures: thumbnails down the left, the main picture in a square frame beside them */}
          <Box sx={{ minWidth: 0, position: { md: "sticky" }, top: 100, display: "flex", flexDirection: { xs: "column-reverse", md: "row" }, gap: 1.5, alignItems: { md: "flex-start" } }}>
            {pictures.length > 1 && (
              <ThumbStrip
                items={pictures}
                selected={image}
                onSelect={setImage}
                perPage={6}
                gap={8}
                thumbSize={58}
                renderThumb={(x, i, active) => (
                  <Box component="img" src={x.src} alt="" sx={{ width: 58, height: 58, flexShrink: 0, objectFit: x.photo ? "cover" : "contain", bgcolor: "#fff", borderRadius: "8px", cursor: "pointer", border: active ? "2px solid #C8A054" : "1px solid #e5e5e5", opacity: active ? 1 : 0.75, transition: "all .15s" }} />
                )}
              />
            )}
            <Box sx={{ position: "relative", flex: 1, minWidth: 0, aspectRatio: "1 / 1", borderRadius: "16px", overflow: "hidden", border: "1px solid #ece6d8", bgcolor: "#faf8f3" }}>
              <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 2, bgcolor: "#C8A054", color: "#fff", px: 1.25, py: 0.4, borderRadius: "6px", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px", lineHeight: 1.3, ...rubikfont }}>
                {dealTag}
              </Box>
              {shown ? (
                <Box component="img" src={shown.src} alt={bogo.title}
                  sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: shown.photo ? "cover" : "contain", p: shown.photo ? 0 : 2, bgcolor: shown.photo ? "transparent" : "#fff", display: "block" }} />
              ) : (
                <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 64 }}>🎁</Box>
              )}
            </Box>
          </Box>

          {/* Everything else in one clean column */}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "3px", color: "#C8A054", textTransform: "uppercase", mb: 0.5, ...rubikfont }}>Special deal</Typography>
            <Typography component="h1" sx={{ fontSize: { xs: "22px", md: "27px" }, fontWeight: 700, color: "#111", lineHeight: 1.25, ...rubikfont }}>{bogo.title}</Typography>
            {bogo.description && (
              <Typography sx={{ fontSize: "14px", color: "#555", mt: 0.75, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", ...rubikfont }}>{bogo.description}</Typography>
            )}

            {products.length > 1 && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.75 }}>
                {products.map((p, i) => (
                  <Box key={p._id} onClick={() => setPick(i)} sx={{ display: "flex", alignItems: "center", gap: 1, p: 0.6, pr: 1.5, borderRadius: "10px", cursor: "pointer", maxWidth: 250, border: i === pick ? "1.5px solid #C8A054" : "1px solid #e8e2d4", bgcolor: i === pick ? "#fbf6ea" : "#fff" }}>
                    <Box component="img" src={resolveOfferImage(p.product_images?.[0])} alt="" sx={{ width: 36, height: 36, objectFit: "contain", borderRadius: "6px" }} />
                    <Typography sx={{ fontSize: "12.5px", fontWeight: i === pick ? 700 : 500, lineHeight: 1.25, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", ...rubikfont }}>{p.title}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {!product && (
              <Box sx={{ mt: 2, p: 2, borderRadius: "12px", bgcolor: "#fbf6ea", border: "1px solid #eadfc2" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, ...rubikfont }}>
                  This deal applies to {categories.length ? categories.map((c) => c.name).join(", ") : "selected categories"}.
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#666", mt: 0.5, ...rubikfont }}>Add {buyQty} product{buyQty > 1 ? "s" : ""} from there and the {free ? "free" : "discounted"} item is applied in your cart.</Typography>
                <Button onClick={() => navigate("/products")} sx={{ mt: 1.5, textTransform: "none", fontWeight: 700, bgcolor: "#af8015", color: "#fff", borderRadius: "20px", px: 3, "&:hover": { bgcolor: "#333" } }}>Browse products</Button>
              </Box>
            )}

            {/* One card: the price split, the total, and the way to buy it */}
            {product && buyMrp > 0 && (
              <Box sx={{ mt: 2, p: 2, borderRadius: "14px", border: "1.5px solid #E8D3A7", background: "linear-gradient(135deg, #FFFDF8 0%, #FFF8EA 100%)" }}>
                {[
                  { label: <>Buy {units} × {money(buyMrp)} <Box component="span" sx={{ color: "#888", fontSize: "12px" }}>MRP</Box></>, value: <b>{money(buyTotal)}</b> },
                  { label: <>Get {freeUnits} × {money(rewardMrp)} <Box component="span" sx={{ color: "#2e7d32", fontWeight: 700 }}>{free ? "FREE" : `${pct}% OFF`}</Box></>, value: <><Box component="span" sx={{ color: "#999", textDecoration: "line-through", mr: 0.75 }}>{money(rewardFull)}</Box><b>{free ? money(0) : money(rewardFull - rewardOff)}</b></> },
                ].map((r, i) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5, fontSize: "14px", color: "#333", ...rubikfont }}>
                    <span>{r.label}</span><span>{r.value}</span>
                  </Box>
                ))}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 0.5, pt: 1, borderTop: "1px dashed #E0CFB3", ...rubikfont }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>You pay for {totalUnits} units</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: "#0F1111" }}>{money(youPay)}</span>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.25, fontSize: "12.5px", ...rubikfont }}>
                  <span style={{ color: "#777" }}>{money(youPay / totalUnits)} per unit · sold at MRP</span>
                  {rewardOff > 0 && <span style={{ color: "#2e7d32", fontWeight: 700 }}>You save {money(rewardOff)}</span>}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 1.75, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, borderRadius: "20px", bgcolor: "#F0F2F2", px: 0.5 }}>
                    <IconButton size="small" aria-label="fewer sets" disabled={sets <= 1} onClick={() => setSets((n) => Math.max(1, n - 1))}><RemoveIcon fontSize="small" /></IconButton>
                    <Typography sx={{ minWidth: 20, textAlign: "center", fontWeight: 700, fontSize: 14 }}>{sets}</Typography>
                    <IconButton size="small" aria-label="more sets" disabled={sets >= maxSets} onClick={() => setSets((n) => Math.min(maxSets, n + 1))}><AddIcon fontSize="small" /></IconButton>
                  </Box>
                  <Button variant="contained" disabled={!available || adding} onClick={addDeal}
                    sx={{ flex: 1, minWidth: 160, bgcolor: "#af8015", color: "#fff", borderRadius: "20px", textTransform: "none", fontWeight: 700, py: 1, "&:hover": { bgcolor: "#333" }, "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#a0a0a0" } }}>
                    {adding ? "Adding…" : "Add deal to cart"}
                  </Button>
                </Box>
                <Typography sx={{ fontSize: "12px", mt: 0.75, fontWeight: 600, color: available ? "#007600" : "#B12704", ...rubikfont }}>{stockText}</Typography>
              </Box>
            )}

            {product && (
              <Button size="small" onClick={() => navigate(`/product/${product._id}`)} endIcon={<LaunchOutlinedIcon />} sx={{ mt: 1.5, px: 0, textTransform: "none", color: "#007185", fontWeight: 600 }}>
                View the regular product page
              </Button>
            )}
          </Box>
        </Box>

        {/* Product details: the same information as the product page */}
        {product && hasDetails && (
          <Box sx={{ mt: { xs: 3, md: 5 }, p: { xs: 2, md: 3 }, borderRadius: "16px", border: "1px solid #ece6d8", bgcolor: "#fff" }}>
            <Typography sx={{ fontSize: { xs: "18px", md: "21px" }, fontWeight: 700, mb: 0.25, ...rubikfont }}>Product details</Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#777", mb: 2, ...rubikfont }}>{details.title}</Typography>
            <Box sx={{ display: "grid", gap: { xs: 2.5, md: 4 }, gridTemplateColumns: { xs: "minmax(0, 1fr)", md: specs.length ? "minmax(0, 360px) minmax(0, 1fr)" : "minmax(0, 1fr)" }, alignItems: "start" }}>
              {specs.length > 0 && (
                <Box sx={{ border: "1px solid #e7e7e7", borderRadius: "6px", overflow: "hidden" }}>
                  {specs.map((x) => (
                    <Box key={x.label} sx={{ display: "flex", borderBottom: "1px solid #e7e7e7", "&:last-child": { borderBottom: "none" } }}>
                      <Box sx={{ width: 130, flexShrink: 0, p: 1, bgcolor: "#f3f3f3", fontWeight: 700, fontSize: "12.5px", textTransform: "capitalize", ...rubikfont }}>{x.label}</Box>
                      <Box sx={{ flex: 1, p: 1, fontSize: "12.5px", textTransform: "capitalize", ...rubikfont }}>{x.value}</Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ minWidth: 0, display: "grid", gap: 2.5 }}>
                {details.key_features && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 1, ...rubikfont }}>About this item</Typography>
                    <Box sx={{ "& ul": { paddingLeft: "18px", margin: 0 }, "& li": { marginBottom: "8px", fontSize: "14px", color: "#0F1111", lineHeight: 1.4, ...rubikfont } }} dangerouslySetInnerHTML={{ __html: cleanHtml(details.key_features) }} />
                  </Box>
                )}
                {details.description && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 1, ...rubikfont }}>Product description</Typography>
                    <Typography sx={{ fontSize: "14px", color: "#0F1111", lineHeight: 1.55, whiteSpace: "pre-line", ...rubikfont }}>{details.description}</Typography>
                  </Box>
                )}
                {details.how_to_use && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 1, ...rubikfont }}>How to use</Typography>
                    <Box sx={{ fontSize: "14px", color: "#0F1111", lineHeight: 1.5, "& ul, & ol": { paddingLeft: "18px", margin: "4px 0" }, "& li": { marginBottom: "6px" }, ...rubikfont }} dangerouslySetInnerHTML={{ __html: cleanHtml(details.how_to_use) }} />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* More to explore */}
        {otherDeals.length > 0 && (
          <Box sx={{ mt: { xs: 4, md: 6 } }}>
            <SectionHeading kicker="More offers" title="Similar Deals" actionLabel="All deals" onAction={() => navigate("/bogo-deals")} />
            <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pt: 1.25, px: 0.75, mx: -0.75, pb: 2, scrollbarWidth: "thin", scrollbarColor: "#C8A054 #f0f0f0", "&::-webkit-scrollbar": { height: 4 }, "&::-webkit-scrollbar-thumb": { background: "#C8A054", borderRadius: 2 } }}>
              {otherDeals.slice(0, 10).map((b) => <BogoCard key={b._id} bogo={b} width={{ xs: 160, sm: 190, md: 210 }} scrollTop />)}
            </Box>
          </Box>
        )}

        {[{ list: similarProducts, kicker: "You may also like", title: "Similar Products" }, { list: relatedProducts, kicker: "Goes well together", title: "Related Products" }].map((sec) => sec.list.length > 0 && (
          <Box key={sec.title} sx={{ mt: { xs: 3, md: 5 } }}>
            <SectionHeading kicker={sec.kicker} title={sec.title} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" }, gap: { xs: 1.25, md: 2 } }}>
              {sec.list.slice(0, 10).map((item) => <ProductCard key={item._id} product={item} />)}
            </Box>
          </Box>
        ))}
      </Container>
    </Box>
  );
};

export default BogoDetails;
